import {
  useInfiniteQuery,
  useQuery
} from '@tanstack/react-query'
import type {
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query'
import axios from 'axios'
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios'
import type {
  GetCourse200,
  GetCourse404,
  GetCourse500,
  GetCourses200,
  GetCourses400,
  GetCourses500,
  GetCoursesParams,
  GetLectureDetail200,
  GetLectureDetail404,
  GetLectureDetail500
} from './types'


type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;



/**
 * 필터링, 정렬, 페이지네이션을 지원하는 강의 목록을 조회합니다.
 * @summary 강의 목록 조회
 */
export const getCourses = (
    params?: GetCoursesParams, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetCourses200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/courses`,{
    ...options,
        params: {...params, ...options?.params},}
    );
  }


export const getGetCoursesQueryKey = (params?: GetCoursesParams,) => {
    return [`https://api.ugot.uk/api/courses`, ...(params ? [params]: [])] as const;
    }

    
export const getGetCoursesInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getCourses>>, TError = AxiosError<GetCourses400 | GetCourses500>>(params?: GetCoursesParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getCourses>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCoursesQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCourses>>> = ({ signal }) => getCourses(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getCourses>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCoursesInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getCourses>>>
export type GetCoursesInfiniteQueryError = AxiosError<GetCourses400 | GetCourses500>

/**
 * @summary 강의 목록 조회
 */
export const useGetCoursesInfinite = <TData = Awaited<ReturnType<typeof getCourses>>, TError = AxiosError<GetCourses400 | GetCourses500>>(
 params?: GetCoursesParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getCourses>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetCoursesInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetCoursesQueryOptions = <TData = Awaited<ReturnType<typeof getCourses>>, TError = AxiosError<GetCourses400 | GetCourses500>>(params?: GetCoursesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCourses>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCoursesQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCourses>>> = ({ signal }) => getCourses(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCourses>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCoursesQueryResult = NonNullable<Awaited<ReturnType<typeof getCourses>>>
export type GetCoursesQueryError = AxiosError<GetCourses400 | GetCourses500>

/**
 * @summary 강의 목록 조회
 */
export const useGetCourses = <TData = Awaited<ReturnType<typeof getCourses>>, TError = AxiosError<GetCourses400 | GetCourses500>>(
 params?: GetCoursesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCourses>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetCoursesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * 지정된 ID의 강의 상세 정보를 조회합니다.
 * @summary 특정 강의 정보 조회
 */
export const getCourse = (
    id: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetCourse200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/courses/${id}`,options
    );
  }


export const getGetCourseQueryKey = (id: number,) => {
    return [`https://api.ugot.uk/api/courses/${id}`] as const;
    }

    
export const getGetCourseInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getCourse>>, TError = AxiosError<GetCourse404 | GetCourse500>>(id: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getCourse>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCourseQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCourse>>> = ({ signal }) => getCourse(id, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getCourse>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCourseInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getCourse>>>
export type GetCourseInfiniteQueryError = AxiosError<GetCourse404 | GetCourse500>

/**
 * @summary 특정 강의 정보 조회
 */
export const useGetCourseInfinite = <TData = Awaited<ReturnType<typeof getCourse>>, TError = AxiosError<GetCourse404 | GetCourse500>>(
 id: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getCourse>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetCourseInfiniteQueryOptions(id,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetCourseQueryOptions = <TData = Awaited<ReturnType<typeof getCourse>>, TError = AxiosError<GetCourse404 | GetCourse500>>(id: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCourse>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCourseQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCourse>>> = ({ signal }) => getCourse(id, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCourse>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCourseQueryResult = NonNullable<Awaited<ReturnType<typeof getCourse>>>
export type GetCourseQueryError = AxiosError<GetCourse404 | GetCourse500>

/**
 * @summary 특정 강의 정보 조회
 */
export const useGetCourse = <TData = Awaited<ReturnType<typeof getCourse>>, TError = AxiosError<GetCourse404 | GetCourse500>>(
 id: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCourse>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetCourseQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * 코스 내 특정 강의 항목(비디오, 마크다운 콘텐츠 등)의 상세 정보를 조회합니다.
 * @summary 특정 강의 항목 상세 조회
 */
export const getLectureDetail = (
    courseId: number,
    lectureId: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetLectureDetail200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/courses/${courseId}/lectures/${lectureId}`,options
    );
  }


export const getGetLectureDetailQueryKey = (courseId: number,
    lectureId: number,) => {
    return [`https://api.ugot.uk/api/courses/${courseId}/lectures/${lectureId}`] as const;
    }

    
export const getGetLectureDetailInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getLectureDetail>>, TError = AxiosError<GetLectureDetail404 | GetLectureDetail500>>(courseId: number,
    lectureId: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getLectureDetail>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLectureDetailQueryKey(courseId,lectureId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLectureDetail>>> = ({ signal }) => getLectureDetail(courseId,lectureId, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(courseId && lectureId),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getLectureDetail>>, TError, TData> & { queryKey: QueryKey }
}

export type GetLectureDetailInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getLectureDetail>>>
export type GetLectureDetailInfiniteQueryError = AxiosError<GetLectureDetail404 | GetLectureDetail500>

/**
 * @summary 특정 강의 항목 상세 조회
 */
export const useGetLectureDetailInfinite = <TData = Awaited<ReturnType<typeof getLectureDetail>>, TError = AxiosError<GetLectureDetail404 | GetLectureDetail500>>(
 courseId: number,
    lectureId: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getLectureDetail>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetLectureDetailInfiniteQueryOptions(courseId,lectureId,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetLectureDetailQueryOptions = <TData = Awaited<ReturnType<typeof getLectureDetail>>, TError = AxiosError<GetLectureDetail404 | GetLectureDetail500>>(courseId: number,
    lectureId: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLectureDetail>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLectureDetailQueryKey(courseId,lectureId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLectureDetail>>> = ({ signal }) => getLectureDetail(courseId,lectureId, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(courseId && lectureId),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getLectureDetail>>, TError, TData> & { queryKey: QueryKey }
}

export type GetLectureDetailQueryResult = NonNullable<Awaited<ReturnType<typeof getLectureDetail>>>
export type GetLectureDetailQueryError = AxiosError<GetLectureDetail404 | GetLectureDetail500>

/**
 * @summary 특정 강의 항목 상세 조회
 */
export const useGetLectureDetail = <TData = Awaited<ReturnType<typeof getLectureDetail>>, TError = AxiosError<GetLectureDetail404 | GetLectureDetail500>>(
 courseId: number,
    lectureId: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLectureDetail>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetLectureDetailQueryOptions(courseId,lectureId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



